/**
 * Sibling product fetcher
 * Resolves sibling product data (image, tags, colour name, legacy ID) by handle
 * via the Shopify Storefront API. Used by Searchspring result cards to enrich
 * the swatch modal without bloating the SS index.
 */

import { gql } from '@apollo/client/core'
import { storefront } from 'libs/storefront/client'
import { localeUrl } from 'helpers/utils'

export interface EnrichedSibling {
  handle: string
  url: string
  title: string
  image?: string
  tags: string[]
  colourName?: string
  productLegacyResourceId?: string
}

const HANDLES_PER_REQUEST = 50

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

function buildSiblingsQuery(handles: string[]) {
  const aliasedFields = handles
    .map(
      (handle, i) => `
        s${i}: product(handle: ${JSON.stringify(handle)}) {
          ...SiblingFields
        }`
    )
    .join('')

  return gql(`
    query SiblingProducts {${aliasedFields}
    }

    fragment SiblingFields on Product {
      id
      handle
      title
      tags
      featuredImage {
        url
      }
      colourName: metafield(namespace: "products", key: "colour_name") {
        value
      }
    }
  `)
}

function nodeToSibling(node: any): EnrichedSibling | null {
  if (!node?.handle) return null
  const gid: string = node.id ?? ''
  const productLegacyResourceId = gid ? gid.split('/').pop() : undefined
  return {
    handle: node.handle,
    url: localeUrl(`products/${node.handle}`),
    title: node.title ?? '',
    image: node.featuredImage?.url ?? undefined,
    tags: Array.isArray(node.tags) ? node.tags : [],
    colourName: node.colourName?.value ?? undefined,
    productLegacyResourceId
  }
}

async function fetchChunk(handles: string[]): Promise<EnrichedSibling[]> {
  if (!handles.length) return []

  try {
    const res = await storefront.query({
      query: buildSiblingsQuery(handles),
      fetchPolicy: 'no-cache'
    })

    const data = res?.data ?? {}
    const out: EnrichedSibling[] = []
    for (let i = 0; i < handles.length; i++) {
      const sibling = nodeToSibling(data[`s${i}`])
      if (sibling) out.push(sibling)
    }
    return out
  } catch (error) {
    console.error('Error fetching sibling products', error)
    return []
  }
}

const cache = new Map<string, EnrichedSibling>()

export async function fetchSiblingProducts(handles: string[]): Promise<EnrichedSibling[]> {
  const cleaned = Array.from(
    new Set(
      (handles || [])
        .filter((h): h is string => typeof h === 'string' && h.length > 0)
        .map((h) => h.trim().toLowerCase())
        .filter(Boolean)
    )
  )
  if (cleaned.length === 0) return []

  const missing = cleaned.filter((h) => !cache.has(h))

  if (missing.length) {
    const chunks = chunk(missing, HANDLES_PER_REQUEST)
    const responses = await Promise.all(chunks.map((c) => fetchChunk(c)))

    for (const list of responses) {
      for (const item of list) {
        cache.set(item.handle, item)
      }
    }
  }

  return cleaned
    .map((h) => cache.get(h))
    .filter((item): item is EnrichedSibling => item !== undefined)
}
